/**
 *
 * This utilizes the Swiper module, see parameters, methods and general APIs here.
 * https://swiperjs.com/element
 *
 * Sample code:
 *
  <swiper-loader loaded-class="announcement-bar-slide-inited">
    <button type="button" data-announcement-bar-prev>Prev</button>
      <swiper-container
        init="false"
        navigation-prev-el="[data-announcement-bar-prev]"
        navigation-next-el="[data-announcement-bar-next]"
        slides-per-view="1"
        autoplay-delay="1000"
      >
      // Sample code for advanced swiper options:
      <script type="application/json" data-json-params>
          {
            "breakpoints": {
              "767": {
                "slidesPerView": "2"
              }
            }
          }
        </script>
        {%- for item in section.blocks -%}
          <swiper-slide class="announcement-bar__slide">Item</swiper-slide>
        {%- endfor -%}
      </swiper-container>
    <button type="button" data-announcement-bar-next>Next</button>
  </swiper-loader>
 */

import { register } from 'swiper/element/bundle';
import { PUB_SUB_EVENTS } from '~/lib/constants';
import { subscribe } from '~/lib/pubsub';

register();

const attributes = {
  loadedClass: 'loaded-class', // class to be added after swiper is initialized
  jsonParams: 'data-json-params', // JSON Params (https://swiperjs.com/swiper-api#parameters)
  jsLike: 'data-js-like',
  playVideoOnSlideChange: 'data-play-video-on-slide-change',
  thumbClass: 'swiper__thumb',
  slideToSelectedVariantImage: 'data-slide-to-selected-variant-image',
};

class SwiperLoader extends HTMLElement {
  constructor() {
    super();
    this.$swiper = this.querySelector('swiper-container');
    this.$swiperSlides = this.querySelectorAll('swiper-slide');
    this.$thumb = this.querySelector(`.${attributes.thumbClass}`);
  }

  connectedCallback() {
    if (this.$swiperSlides.length > 0) {
      customElements.whenDefined('swiper-container').then(() => {
        this.initSwiper();
      });
    }

    if (this.hasAttribute(attributes.slideToSelectedVariantImage)) {
      this.slideToSelectedVariantImage();
    }
  }

  initSwiper() {
    let $jsonScript = this.$swiper.querySelector(`[${attributes.jsonParams}]`);

    let jsonParams = {};

    try {
      if ($jsonScript) {
        jsonParams = $jsonScript.hasAttribute(attributes.jsLike)
          ? new Function(`return ${$jsonScript.innerHTML.trim()}`)()
          : JSON.parse($jsonScript.innerHTML);
      }

      const params = {
        ...jsonParams,
      };

      Object.assign(this.$swiper, params);

      this.$swiper.initialize();
      this.attachEvents();
    } catch (error) {
      console.log(error);
    }

    this.hasAttribute(attributes.loadedClass) && document.body.classList.add(this.getAttribute(attributes.loadedClass));
  }

  attachEvents() {
    this.$swiper.swiper.on('slideChange', () => {
      if (this.$swiper.swiper.realIndex !== 0) {
        this.$swiperSlides.forEach((slide) => {
          slide.classList.remove('scroll-trigger', 'animate--slide-in');
        });
      }
    });

    if (this.hasAttribute(attributes.playVideoOnSlideChange)) {
      this.$swiper.swiper.on('slideChange', () => {
        const activeSlide = this.$swiper.swiper.slides[this.$swiper.swiper.activeIndex];
        const video = activeSlide.querySelector('video');

        if (video) {
          video.currentTime = 0;
          video.play();
        }
      });
    }
  }

  slideToSelectedVariantImage() {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const variant = data.variant;
      if (!variant.featured_media) return;
      const variantImagePosition = variant.featured_media.position;
      this.$swiper.swiper.slideTo(variantImagePosition - 1);
    });
  }
}

customElements.define('swiper-loader', SwiperLoader);
